import type { CSSProperties } from 'react';
import type { StatusChipTone } from 'utils/badgeColors';
import { CLAIM_TABLE_SURFACE_COLOR } from 'styles/ClaimTableStyles';

export const PROVIDER_CLAIMS_ACCENT_COLOR =
  'var(--mantine-primary-color-filled)';

export const PROVIDER_CLAIMS_SURFACE_COLOR = CLAIM_TABLE_SURFACE_COLOR;

const providerTabBaseStyle: CSSProperties = {
  flexShrink: 0,
  borderRadius: 0,
  borderBottomWidth: 2,
  background: 'transparent',
  color: 'var(--mantine-color-text)',
  fontWeight: 600,
};

const providerTabActiveStyle: CSSProperties = {
  color: PROVIDER_CLAIMS_ACCENT_COLOR,
  borderBottomColor: PROVIDER_CLAIMS_ACCENT_COLOR,
  fontWeight: 700,
};

export const providerClaimTabStyles = {
  getList: (colorScheme: string): CSSProperties => ({
    background:
      colorScheme === 'dark'
        ? 'var(--mantine-color-dark-8)'
        : 'var(--mantine-color-gray-1)',
    boxShadow: 'inset 0 -1px 0 var(--mantine-color-default-border)',
    paddingInline: 'var(--mantine-spacing-sm)',
    width: '100%',
    flexWrap: 'nowrap',
    overflowX: 'auto',
  }),
  description: {
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
    borderBottom: '1px solid var(--mantine-color-default-border)',
  } satisfies CSSProperties,
  infoIcon: {
    color: PROVIDER_CLAIMS_ACCENT_COLOR,
  } satisfies CSSProperties,
  getTab: (active: boolean): CSSProperties => ({
    ...providerTabBaseStyle,
    borderBottomColor: 'transparent',
    ...(active ? providerTabActiveStyle : {}),
  }),
  getCount: (active: boolean, actionNeededCount?: number): CSSProperties => {
    const isActionNeeded = actionNeededCount !== undefined;
    const hasActions = isActionNeeded && actionNeededCount > 0;
    const appearance = {
      background: 'var(--mantine-color-default-hover)',
      color: 'var(--mantine-color-dimmed)',
    };
    if (hasActions) {
      appearance.background = 'var(--mantine-color-red-light)';
      appearance.color = 'var(--mantine-color-red-light-color)';
    } else if (active && !isActionNeeded) {
      appearance.background = 'var(--mantine-primary-color-light)';
      appearance.color = PROVIDER_CLAIMS_ACCENT_COLOR;
    }
    return {
      display: 'inline-block',
      minWidth: 34,
      padding: '2px 8px',
      borderRadius: 'var(--mantine-radius-xl)',
      ...appearance,
      fontSize: 'var(--mantine-font-size-xs)',
      fontWeight: 600,
      lineHeight: 1.2,
      textAlign: 'center',
    };
  },
  actionNeededIcon: {
    color: 'var(--mantine-color-red-light-color)',
  } satisfies CSSProperties,
};

const getLegendItemBorder = (grouped: boolean): CSSProperties => {
  if (grouped) {
    return {
      paddingLeft: 'var(--mantine-spacing-sm)',
      borderLeft: '1px solid var(--mantine-color-default-border)',
    };
  }
  return {
    paddingTop: 'var(--mantine-spacing-sm)',
    borderTop: '1px solid var(--mantine-color-default-border)',
  };
};

export const providerClaimLegendStyles = {
  getStatusChip: (palette: StatusChipTone): CSSProperties => ({
    backgroundColor: palette.backgroundColor,
    border: `1px solid ${palette.borderColor}`,
    color: palette.textColor,
  }),
  infoIcon: {
    flexShrink: 0,
    marginTop: 2,
    color: PROVIDER_CLAIMS_ACCENT_COLOR,
  } satisfies CSSProperties,
  inlineControl: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginInline: 2,
  } satisfies CSSProperties,
  searchBar: {
    flexShrink: 0,
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-lg)',
    borderBottom: '1px solid var(--mantine-color-default-border)',
  } satisfies CSSProperties,
  content: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding:
      'var(--mantine-spacing-md) var(--mantine-spacing-lg) var(--mantine-spacing-xl)',
  } satisfies CSSProperties,
  workflowBar: {
    background: PROVIDER_CLAIMS_SURFACE_COLOR,
    borderBottom: '1px solid var(--mantine-color-default-border)',
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
  } satisfies CSSProperties,
  triggerContainer: {
    flex: 'none',
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingRight: 'var(--mantine-spacing-md)',
    marginRight: 'var(--mantine-spacing-sm)',
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderRightColor: 'var(--mantine-color-default-border)',
  } satisfies CSSProperties,
  workflowLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: 'var(--mantine-color-dimmed)',
    cursor: 'help',
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  } satisfies CSSProperties,
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: PROVIDER_CLAIMS_ACCENT_COLOR,
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  } satisfies CSSProperties,
  questionIcon: {
    width: 14,
    height: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: PROVIDER_CLAIMS_ACCENT_COLOR,
    borderRadius: '50%',
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1,
  } satisfies CSSProperties,
  drawer: {
    header: {
      flexShrink: 0,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      borderBottomColor: 'var(--mantine-color-default-border)',
      padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
    } satisfies CSSProperties,
    title: {
      fontWeight: 700,
    } satisfies CSSProperties,
    body: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    } satisfies CSSProperties,
    content: {
      display: 'flex',
      flexDirection: 'column',
      borderLeftWidth: 1,
      borderLeftStyle: 'solid',
      borderLeftColor: 'var(--mantine-color-default-border)',
      boxShadow: 'var(--mantine-shadow-xl)',
    } satisfies CSSProperties,
  },
  visibilityCallout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--mantine-spacing-sm)',
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-md)',
    background: 'var(--mantine-color-default-hover)',
  } satisfies CSSProperties,
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--mantine-spacing-sm)',
    padding: 'var(--mantine-spacing-md)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-lg)',
    background: 'transparent',
  } satisfies CSSProperties,
  getItem: (grouped: boolean): CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 3,
    ...getLegendItemBorder(grouped),
  }),
  groupCallout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--mantine-spacing-sm)',
    padding: 'var(--mantine-spacing-sm)',
    border: '1px solid var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-sm)',
    background: PROVIDER_CLAIMS_SURFACE_COLOR,
  } satisfies CSSProperties,
  groupCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--mantine-spacing-sm)',
    marginTop: 2,
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-md)',
    background: 'var(--mantine-color-default-hover)',
  } satisfies CSSProperties,
  getStageNumber: (palette: StatusChipTone): CSSProperties => ({
    width: 22,
    height: 22,
    display: 'inline-flex',
    flex: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: palette.backgroundColor,
    color: palette.textColor,
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 700,
  }),
};

export const providerClaimFilterStyles = {
  getNeedsYouFilter: (active: boolean): CSSProperties => ({
    height: 30,
    fontWeight: 600,
    background: active ? 'var(--mantine-color-red-9)' : 'transparent',
    color: active ? 'var(--mantine-color-white)' : 'var(--mantine-color-text)',
    borderColor: active
      ? 'var(--mantine-color-red-9)'
      : 'var(--mantine-color-default-border)',
  }),
  darkNeedsYou: {
    banner: {
      background:
        'color-mix(in srgb, var(--mantine-color-red-9) 14%, transparent)',
      borderColor: 'var(--mantine-color-red-8)',
    } satisfies CSSProperties,
    icon: {
      background: 'var(--mantine-color-red-9)',
    } satisfies CSSProperties,
    badge: {
      background: 'var(--mantine-color-white)',
      color: 'var(--mantine-color-red-9)',
      borderColor: 'var(--mantine-color-red-8)',
      opacity: 1,
    } satisfies CSSProperties,
    title: {
      color: 'var(--mantine-color-white)',
    } satisfies CSSProperties,
    body: {
      color: 'var(--mantine-color-gray-4)',
    } satisfies CSSProperties,
    button: {
      background: 'var(--mantine-color-red-9)',
      color: 'var(--mantine-color-white)',
      borderColor: 'var(--mantine-color-red-9)',
    } satisfies CSSProperties,
  },
  needsYouBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--mantine-spacing-sm)',
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--mantine-color-red-6)',
    borderLeftWidth: 4,
    borderRadius: 'var(--mantine-radius-md)',
    background: 'var(--mantine-color-red-light)',
  } satisfies CSSProperties,
  needsYouIconContainer: {
    position: 'relative',
    width: 32,
    height: 32,
    display: 'inline-flex',
    flex: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'var(--mantine-color-red-filled)',
  } satisfies CSSProperties,
  needsYouCountBadge: {
    position: 'absolute',
    top: -5,
    right: -5,
    minWidth: 19,
    height: 19,
    padding: '0 4px',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--mantine-color-red-filled)',
    background: 'var(--mantine-color-body)',
    color: 'var(--mantine-color-red-filled)',
    fontSize: 10,
    lineHeight: 1,
  } satisfies CSSProperties,
  selectorButton: {
    height: 30,
    color: 'var(--mantine-color-text)',
    whiteSpace: 'nowrap',
  } satisfies CSSProperties,
  dateDropdown: {
    maxWidth: 'calc(100vw - var(--mantine-spacing-md) * 2)',
    overflowX: 'auto',
  } satisfies CSSProperties,
  presetColumn: {
    minWidth: 150,
    padding: 'var(--mantine-spacing-sm)',
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderRightColor: 'var(--mantine-color-default-border)',
    background: 'var(--mantine-color-default-hover)',
  } satisfies CSSProperties,
  dropdownLabel: {
    padding: '7px 9px 5px',
    color: 'var(--mantine-color-dimmed)',
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  } satisfies CSSProperties,
  payorCheckbox: {
    root: {
      padding: '7px 9px',
      borderRadius: 'var(--mantine-radius-sm)',
    },
    body: {
      width: '100%',
      alignItems: 'center',
    },
    labelWrapper: {
      flex: 1,
    },
  },
};
